import type { SpeechSynthesisAdapter } from "@assistant-ui/react";
import { aiAgentClient } from "../api/ai-agent-client";

/**
 * OpenAI TTS adapter for assistant-ui
 *
 * This adapter uses OpenAI's Text-to-Speech API via backend to synthesize speech
 * from text messages in the AI assistant.
 */
export class OpenAITTSAdapter implements SpeechSynthesisAdapter {
  private currentAudio: HTMLAudioElement | null = null;
  private currentUtterance: SpeechSynthesisAdapter.Utterance | null = null;
  private abortController: AbortController | null = null;
  private salonId: string;
  private voice: string;

  constructor(options: {
    salonId: string;
    voice?: string;
  }) {
    this.salonId = options.salonId;
    this.voice = options?.voice || "alloy";
  }

  speak(text: string): SpeechSynthesisAdapter.Utterance {
    // Cancel any currently playing audio and pending requests
    this.cancel();

    // Create new abort controller for this request
    this.abortController = new AbortController();

    // Create status holder
    let currentStatus: SpeechSynthesisAdapter.Utterance["status"] = { type: "starting" };
    const listeners = new Set<() => void>();

    const notifyListeners = () => {
      listeners.forEach((callback) => {
        callback();
      });
    };

    const updateStatus = (newStatus: SpeechSynthesisAdapter.Utterance["status"]) => {
      currentStatus = newStatus;
      notifyListeners();
    };

    // Create utterance object
    const utterance: SpeechSynthesisAdapter.Utterance = {
      get status() {
        return currentStatus;
      },
      subscribe(callback: () => void) {
        listeners.add(callback);
        return () => {
          listeners.delete(callback);
        };
      },
      cancel: () => {
        this.cancel();
      },
    };

    // Store current utterance
    this.currentUtterance = utterance;

    // Start async TTS generation
    this.generateSpeech(text, updateStatus, this.abortController.signal).catch((error) => {
      // Only update status if not aborted
      if (error.name !== "AbortError") {
        console.error("[OpenAITTSAdapter] Error:", error);
        updateStatus({ type: "ended", reason: "error", error });
      }
    });

    return utterance;
  }

  private async generateSpeech(
    text: string,
    updateStatus: (status: SpeechSynthesisAdapter.Utterance["status"]) => void,
    signal: AbortSignal
  ): Promise<void> {
    try {
      // Call backend TTS endpoint
      const audioBlob = await aiAgentClient.synthesizeSpeech(this.salonId, text, this.voice);
      const audioUrl = URL.createObjectURL(audioBlob);

      // Check if aborted before creating audio
      if (signal.aborted) {
        URL.revokeObjectURL(audioUrl);
        return;
      }

      // Create audio element
      const audio = new Audio(audioUrl);
      this.currentAudio = audio;

      // Set up event listeners BEFORE playing
      audio.onplaying = () => {
        updateStatus({ type: "running" });
      };

      audio.onended = () => {
        updateStatus({ type: "ended", reason: "finished" });
        URL.revokeObjectURL(audioUrl);
        if (this.currentAudio === audio) {
          this.currentAudio = null;
        }
      };

      audio.onerror = (event) => {
        console.error("[OpenAITTSAdapter] Audio playback error:", event);
        // Get actual error from audio element
        const audioError = audio.error;
        console.error("[OpenAITTSAdapter] Audio error details:", {
          code: audioError?.code,
          message: audioError?.message,
        });
        updateStatus({ type: "ended", reason: "error", error: audioError });
        URL.revokeObjectURL(audioUrl);
        if (this.currentAudio === audio) {
          this.currentAudio = null;
        }
      };

      // Listen for abort signal
      const abortHandler = () => {
        // Remove event listeners to prevent errors
        audio.onplaying = null;
        audio.onended = null;
        audio.onerror = null;

        // Pause and cleanup
        audio.pause();
        URL.revokeObjectURL(audioUrl);
        if (this.currentAudio === audio) {
          this.currentAudio = null;
        }
        updateStatus({ type: "ended", reason: "cancelled" });
      };

      signal.addEventListener("abort", abortHandler, { once: true });

      // Play the audio
      try {
        await audio.play();
      } catch (playError) {
        console.error("[OpenAITTSAdapter] Play failed:", playError);
        updateStatus({ type: "ended", reason: "error", error: playError });
        URL.revokeObjectURL(audioUrl);
        if (this.currentAudio === audio) {
          this.currentAudio = null;
        }
        throw playError;
      }
    } catch (error) {
      this.currentAudio = null;
      throw error;
    }
  }

  cancel(): void {
    // Abort any pending fetch requests (this will trigger abort listeners)
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Stop current audio (don't clear src here, let abort listener handle cleanup)
    if (this.currentAudio) {
      this.currentAudio.pause();
      // Note: cleanup is handled by abort listener
    }

    // Clear current utterance reference
    if (this.currentUtterance) {
      this.currentUtterance = null;
    }
  }
}
